<template>
  <div class="account-selector" v-if="showSelector">
    <label class="account-label">Active Account:</label>
    <select
      :value="selectedAccountId"
      @change="handleAccountChange"
      class="account-select"
    >
      <option
        v-for="account in accounts"
        :key="account.id"
        :value="account.id"
      >
        {{ account.name }} ({{ account.type }})
      </option>

      <!-- Super admins used to see every account's data regardless of what
           was selected here, which made the whole control look broken. Now
           the selection scopes the data, and seeing everything is this
           explicit choice instead of a silent side effect of being admin. -->
      <option v-if="isSuperAdmin" :value="ALL_ACCOUNTS.id">
        — All accounts —
      </option>
    </select>
  </div>
  <div class="account-display" v-else-if="activeAccount">
    <span class="account-label">Account:</span>
    <span class="account-name">{{ activeAccount.name }}</span>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { ALL_ACCOUNTS } from "@/store/services/auth";
const toast = useToast();

export default {
  name: "AccountSelector",

  data() {
    return { ALL_ACCOUNTS };
  },

  computed: {
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin;
    },

    // A super admin always gets the control, even with one account, because
    // "All accounts" is a meaningful second choice for them.
    showSelector() {
      return (this.accounts && this.accounts.length > 1) || this.isSuperAdmin;
    },

    accounts() {
      return this.$store.state.auth.accounts || [];
    },
    
    activeAccount() {
      return this.$store.state.auth.activeAccount;
    },
    
    selectedAccountId() {
      return this.activeAccount?.id || null;
    }
  },
  
  methods: {
    handleAccountChange(event) {
      const raw = event.target.value;
      const account = raw === ALL_ACCOUNTS.id
        ? ALL_ACCOUNTS
        : this.accounts.find(acc => acc.id === parseInt(raw));
      if (!account) return;

      // No namespace prefix. None of this app's Vuex modules set
      // `namespaced: true`, so their actions live in the root namespace —
      // every other call site in the app uses the bare name. Dispatching
      // 'auth/switchAccount' matched no handler at all: Vuex logged "unknown
      // action type" to the console and returned, the active account never
      // changed, the X-Account-ID header never changed, and so switching
      // accounts appeared to do nothing. It was doing nothing.
      this.$store.dispatch('switchAccount', account);

      toast.success(`Switched to ${account.name}`);

      // Nothing to refresh by hand. App.vue keys <RouterView> on the active
      // account id, so the whole view remounts and refetches. The method that
      // used to live here was an empty body with a comment claiming Vuex
      // watchers handled it — only 4 of 52 views had such a watcher.
    }
  }
};
</script>

<style scoped>
.account-selector {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.account-label {
  display: block;
  color: #fff;
  font-size: 12px;
  margin-bottom: 5px;
  font-weight: 500;
}

.account-select {
  width: 100%;
  padding: 8px 12px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
}

.account-select:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.5);
}

.account-select:focus {
  background: rgba(255, 255, 255, 0.2);
  border-color: #6a5cff;
}

.account-select option {
  background: #27253f;
  color: #fff;
  padding: 10px;
}

.account-display {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.account-name {
  color: #fff;
  font-weight: 500;
  font-size: 14px;
}
</style>
